import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { OrganizationRootDetails, OrganizationRootDetailsAdapter } from "@/core/models/organizationRootDetails";
import { OrganizationRootDetailApi } from "@/apps/shared/organization/core/infrastructure/repository/OrganizationRootDetailApi";

const ORD_LOCAL_STORAGE_KEY = "cx-organization-root-detail";

const ordStorage = {
  get: (): OrganizationRootDetails | undefined => {
    const storedOrdApi = window.localStorage.getItem(ORD_LOCAL_STORAGE_KEY);
    if (!storedOrdApi) {
      return;
    }

    const ordApi = JSON.parse(storedOrdApi);
    return OrganizationRootDetailsAdapter.adapt(ordApi);
  },
  set: (ordApi: OrganizationRootDetailApi): void => {
    window.localStorage.setItem(ORD_LOCAL_STORAGE_KEY, JSON.stringify(ordApi));
  },
  reset: (): void => {
    window.localStorage.removeItem(ORD_LOCAL_STORAGE_KEY);
  },
};

const useOrganizationRootDetailsStore = defineStore("organizationRootDetails", () => {
  const organizationRootDetails = ref<OrganizationRootDetails | undefined>(ordStorage.get());
  const isCategoryNavigationEnabled = computed(() => organizationRootDetails.value?.categoryNavigationEnabled);
  const isTagPriceEnabled = computed(() => organizationRootDetails.value?.tagPriceEnabled);

  const isThisFeatureEnabled = (feature: string) => {
    if (organizationRootDetails.value === undefined) {
      return false;
    }
    return organizationRootDetails.value.enabledPremiumFeatures.includes(feature);
  };

  const setFromOrdApi = (ordApi: OrganizationRootDetailApi): void => {
    ordStorage.set(ordApi);
    organizationRootDetails.value = OrganizationRootDetailsAdapter.adapt(ordApi);
  };

  const reset = (): void => {
    ordStorage.reset();
    organizationRootDetails.value = undefined;
  };

  return {
    organizationRootDetails,
    isCategoryNavigationEnabled,
    isTagPriceEnabled,
    isThisFeatureEnabled,
    setFromOrdApi,
    reset,
  };
});

export default useOrganizationRootDetailsStore;
